import { addCommasToNumber, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card } from 'antd'
import React, { useMemo } from 'react'

import type { CreditRebillResponse, Invoice } from '../../../../../api/useFreight/types'
import { calculateTransactionAmounts, calculateTotalMiles } from '../utils/calculations'

interface InvoiceSummaryCardsProps {
  invoice: Invoice
  selectedAccessorialType: 'Base Freight' | 'Accessorial' | 'Surcharge' | null
  setSelectedAccessorialType: (type: 'Base Freight' | 'Accessorial' | 'Surcharge' | null) => void
  creditRebillMetadata: CreditRebillResponse
}

// Helper component to display the difference
function DifferenceIndicator({ diff }: { diff?: number | null | undefined }) {
  // If diff is undefined, return null
  if (diff === undefined || diff === null) return null

  // If diff is 0, show a neutral indicator
  if (diff === 0) {
    return (
      <Horizontal verticalCenter gap={4} style={{ color: 'var(--theme-text-light)' }}>
        <MaterialIcon icon='remove' size={16} />
        <Texto category='p2' style={{ color: 'var(--theme-text-light)' }}>
          $0.00
        </Texto>
      </Horizontal>
    )
  }

  const isPositive = diff > 0
  const color = isPositive ? 'var(--theme-success)' : 'var(--theme-error)'
  const icon = isPositive ? 'arrow_upward' : 'arrow_downward'

  return (
    <Horizontal verticalCenter gap={4} style={{ color }}>
      <MaterialIcon icon={icon} size={16} />
      <Texto weight='bold' category='p2' style={{ color, whiteSpace: 'nowrap' }}>
        $
        {Math.abs(diff).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </Texto>
    </Horizontal>
  )
}

export function InvoiceSummaryCards({
  invoice,
  selectedAccessorialType,
  setSelectedAccessorialType,
  creditRebillMetadata,
}: InvoiceSummaryCardsProps) {
  // Calculate total miles using the utility function
  const miles = calculateTotalMiles(invoice)

  // Helper function to get card style based on selection state
  const getCardStyle = (type: 'Base Freight' | 'Accessorial' | 'Surcharge') => {
    const baseStyle = { padding: '10px 4px' }
    if (selectedAccessorialType === type) {
      return {
        ...baseStyle,
        borderColor: 'var(--theme-color-2)',
        backgroundColor: 'var(--theme-color-2-dim)',
      }
    }
    return baseStyle
  }

  // Use the utility function to calculate transaction amounts
  const calculatedAmounts = useMemo(() => {
    return calculateTransactionAmounts(invoice)
  }, [invoice])

  const baseDelta = creditRebillMetadata
    ? creditRebillMetadata?.rebill_base_diff
    : invoice.credit_rebill_metadata?.delta_base_total
  const accessorialDelta = creditRebillMetadata
    ? creditRebillMetadata?.rebill_accessorial_diff
    : invoice.credit_rebill_metadata?.delta_accessorial_total
  const surchargeDelta = creditRebillMetadata
    ? creditRebillMetadata?.rebill_surcharge_diff
    : invoice.credit_rebill_metadata?.delta_surcharge_total

  const totalDelta = creditRebillMetadata
    ? creditRebillMetadata?.rebill_diff
    : invoice.credit_rebill_metadata?.delta_total

  // Use calculated amounts in rebill mode, otherwise use invoice properties
  const isRebillMode = invoice.type === 'rebill'
  const displayBaseAmount = isRebillMode ? calculatedAmounts.baseAmount : invoice.base_amount
  const displayAccessorialAmount = isRebillMode ? calculatedAmounts.accessorialAmount : invoice.accessorial_amount
  const displaySurchargeAmount = isRebillMode ? calculatedAmounts.surchargeAmount : invoice.surcharge_amount
  const displayTotalAmount = isRebillMode ? calculatedAmounts.totalAmount : invoice.total_amount

  return (
    <Horizontal verticalCenter gap={16} flex='0' style={{ overflow: 'visible' }}>
      <Card size='small' className='flex-1' style={getCardStyle('Base Freight')}>
        <Horizontal verticalCenter gap={8} justifyContent='space-between' style={{ height: '100%' }}>
          <Vertical style={{ minWidth: 0 }}>
            <Texto category='label' appearance='light'>
              Base Freight
            </Texto>
            <Texto
              category='h4'
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              $
              {displayBaseAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Texto>
            {baseDelta !== 0 && <DifferenceIndicator diff={baseDelta} />}
          </Vertical>
          {selectedAccessorialType === 'Base Freight' ? (
            <GraviButton
              appearance='filled'
              size='small'
              icon={<MaterialIcon icon='close' />}
              buttonText='Deselect'
              onClick={() => setSelectedAccessorialType(null)}
              disabled={displayBaseAmount === 0}
            />
          ) : (
            <GraviButton
              appearance='filled'
              size='small'
              iconPosition='end'
              buttonText='View'
              icon={<MaterialIcon className='pt-1' icon='arrow_right_alt' />}
              onClick={() => {
                setSelectedAccessorialType('Base Freight')
              }}
              disabled={displayBaseAmount === 0}
            />
          )}
        </Horizontal>
      </Card>
      <Card size='small' className='flex-1' style={getCardStyle('Accessorial')}>
        <Horizontal verticalCenter gap={8} justifyContent='space-between' style={{ height: '100%' }}>
          <Vertical style={{ minWidth: 0 }}>
            <Texto category='label' appearance='light'>
              Accessorials
            </Texto>
            <Texto
              category='h4'
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              $
              {displayAccessorialAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Texto>
            {accessorialDelta !== 0 && <DifferenceIndicator diff={accessorialDelta} />}
          </Vertical>
          {selectedAccessorialType === 'Accessorial' ? (
            <GraviButton
              appearance='filled'
              size='small'
              icon={<MaterialIcon icon='close' />}
              buttonText='Deselect'
              onClick={() => setSelectedAccessorialType(null)}
              disabled={displayAccessorialAmount === 0}
            />
          ) : (
            <GraviButton
              appearance='filled'
              size='small'
              iconPosition='end'
              buttonText='View'
              icon={<MaterialIcon className='pt-1' icon='arrow_right_alt' />}
              onClick={() => {
                setSelectedAccessorialType('Accessorial')
              }}
              disabled={displayAccessorialAmount === 0}
            />
          )}
        </Horizontal>
      </Card>
      <Card size='small' className='flex-1' style={getCardStyle('Surcharge')}>
        <Horizontal verticalCenter gap={8} justifyContent='space-between' style={{ height: '100%' }}>
          <Vertical style={{ minWidth: 0 }}>
            <Texto category='label' appearance='light'>
              Surcharge
            </Texto>
            <Texto
              category='h4'
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              $
              {displaySurchargeAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Texto>
            {surchargeDelta !== 0 && <DifferenceIndicator diff={surchargeDelta} />}
          </Vertical>
          {selectedAccessorialType === 'Surcharge' ? (
            <GraviButton
              appearance='filled'
              size='small'
              icon={<MaterialIcon icon='close' />}
              buttonText='Deselect'
              onClick={() => setSelectedAccessorialType(null)}
              disabled={displaySurchargeAmount === 0}
            />
          ) : (
            <GraviButton
              appearance='filled'
              size='small'
              iconPosition='end'
              buttonText='View'
              icon={<MaterialIcon className='pt-1' icon='arrow_right_alt' />}
              onClick={() => {
                setSelectedAccessorialType('Surcharge')
              }}
              disabled={displaySurchargeAmount === 0}
            />
          )}
        </Horizontal>
      </Card>
      <Card size='small' style={{ flex: 1.7, padding: '10px 4px', height: '100px' }}>
        <Horizontal verticalCenter gap={20} style={{ height: '100%' }}>
          <Vertical flex={1} border='right' style={{ minWidth: 0 }}>
            <Texto category='label' appearance='light'>
              Orders
            </Texto>
            <Horizontal verticalCenter gap={6}>
              <MaterialIcon icon='local_shipping' size={20} />
              <Texto
                category='h4'
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {invoice.transactions.length}
              </Texto>
            </Horizontal>
          </Vertical>
          <Vertical flex={1.2} border='right' style={{ minWidth: 0 }}>
            <Texto category='label' appearance='light'>
              Miles Driven
            </Texto>
            <Horizontal verticalCenter gap={6}>
              <MaterialIcon icon='route' size={20} />
              <Texto
                category='h4'
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {addCommasToNumber(miles)}
              </Texto>
            </Horizontal>
          </Vertical>
          <Vertical alignItems='flex-end' flex='0' style={{ overflow: 'visible' }}>
            <Texto category='label' style={{ whiteSpace: 'nowrap' }} appearance='light'>
              Invoice Total
            </Texto>
            <Texto
              category='h3'
              weight='bold'
              style={{
                textOverflow: 'ellipsis',
                overflow: 'visible',
                whiteSpace: 'nowrap',
              }}
            >
              $
              {displayTotalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Texto>
            {totalDelta !== 0 && <DifferenceIndicator diff={totalDelta} />}
          </Vertical>
        </Horizontal>
      </Card>
    </Horizontal>
  )
}
